import { FC, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuthStatus } from "../lib/stores/auth-store";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { InfoIcon } from "lucide-react";
import { z } from "zod";
import logoSna from "../assets/img/logo-sna-transparent.png";
import logoAdf from "../assets/img/logo-adf.png";
import logoApiconnect from "../assets/img/logo.png";
import Divider from "../components/ui/Divider";
import ResetPasswordDialog from "../components/dialogs/ResetPasswordDialog";

const apiUrl = import.meta.env.VITE_API_URL;

interface CheckingAccountResult {
  ficheFound: boolean;
  isRegistered: boolean;
}

const AuthPage: FC = () => {
  const [error, setError] = useState("");
  const [parent] = useAutoAnimate();
  const [loading, setLoading] = useState(false);
  const [signUpMode, setSignUpMode] = useState(false);
  const [hasAccount, setHasAccount] = useState(false);
  const [premiereCo, setPremiereCo] = useState(false);
  const [email, setEmail] = useState(import.meta.env.DEV ? "stephane.aletru@gmail.com" : "");
  const [password, setPassword] = useState(import.meta.env.DEV ? "Maisputain3" : "");
  const [confirmPassword, setConfirmPassword] = useState("");
  const navigate = useNavigate();
  const setIsLoggedIn = useAuthStatus((state) => state.setIsLoggedIn);
  const isLoggedIn = useAuthStatus((state) => state.isLoggedIn);

  if (isLoggedIn) {
    return <Navigate to={"/"} replace />;
  }

  const handleLogin = async () => {
    setLoading(true);
    try {
      const { data: tokens } = await axios.post(`${import.meta.env.VITE_API_URL}/auth/login`, {
        email: email.trim().toLowerCase(),
        password,
      });
      // Save le token dans localstorage
      localStorage.setItem("accessToken", tokens.accessToken);
      localStorage.setItem("refreshToken", tokens.refreshToken);
      setIsLoggedIn(true);
      navigate("/", { replace: true });
    } catch (error) {
      console.error(error);
      setLoading(false);
      setError("Mot de passe incorrect");
    }
  };

  const handleSignUp = async () => {
    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }
    const schema = z
      .string()
      .trim()
      .min(8, "Le mot de passe doit faire 8 caractères minimum")
      .max(32, "Le mot de passe peut faire 32 caractères maximum")
      .regex(
        RegExp("^(?=(.*[a-z]){1,})(?=(.*[A-Z]){1,})(?=(.*[0-9]){1,}).{8,}$"),
        "Votre mot de passe doit contenir au minimum une lettre en majuscule, une lettre en minuscule et un chiffre."
      );
    const valid = schema.safeParse(password);
    if (!valid.success) {
      setError(valid.error.issues[0].message);
      return;
    }
    try {
      setLoading(true);
      const { data: tokens } = await axios.post(`${apiUrl}/auth/register`, {
        email: email.trim().toLowerCase(),
        password,
      });
      // Save le token dans localstorage
      localStorage.setItem("accessToken", tokens.accessToken);
      localStorage.setItem("refreshToken", tokens.refreshToken);
      setIsLoggedIn(true);
      navigate("/", { replace: true });
    } catch (error) {
      setLoading(false);
      setError("Une erreur s'est produite");
    }
  };

  const verifyAccount = async (email: string) => {
    try {
      setLoading(true);
      const { data }: { data: CheckingAccountResult } = await axios.post(
        `${import.meta.env.VITE_API_URL}/users/check-user`,
        { email: email.trim().toLowerCase() }
      );
      if (data.isRegistered && data.ficheFound) {
        setHasAccount(true);
      } else if (!data.isRegistered && data.ficheFound) {
        // Envoyer mail de reset password et mettre un message Alert
        await axios.post(`${apiUrl}/auth/send-reset-password-email`, { email });
        setPremiereCo(true);
      } else {
        setSignUpMode(true);
      }
    } catch (error) {
      console.error(error);
      setError("Une erreur s'est produite");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setError("");
    if (hasAccount || premiereCo) {
      await handleLogin();
    } else if (signUpMode) {
      await handleSignUp();
    } else {
      await verifyAccount(email);
    }
  };

  return (
    <div className='p-4 bg-slate-50'>
      <div className='text-center'>
        <div>
          <img className='mx-auto h-28' src={logoApiconnect} alt='Logo ApiConnect' />
          <Divider className='w-96 my-4 mx-auto' />
          <div className='flex justify-center gap-2'>
            <div className='w-48'>
              <img src={logoAdf} alt="Logo de l'Abeille de France" />
            </div>
            <div className='w-32'>
              <img src={logoSna} alt="Logo du Syndicat National d'Apiculture" />
            </div>
          </div>
          <Divider className='w-96 my-4 mx-auto' />
        </div>
        <div className='font-bold text-3xl my-3'>Connexion</div>
        <div className='mb-5'>Veuillez saisir votre adresse email pour démarrer.</div>
      </div>

      <form className='bg-white shadow-md rounded-xl max-w-[450px] mx-auto p-8' onSubmit={handleSubmit} ref={parent}>
        {(hasAccount || premiereCo || signUpMode) && (
          <div className='p-4 bg-blue-200 rounded-md mb-5 flex flex-row gap-4'>
            <div>
              <InfoIcon className='stroke-blue-700 h-4 w-4 mt-1' />
            </div>
            <div>
              {hasAccount && <>Votre compte a été trouvé, veuillez saisir votre mot de passe pour vous connecter.</>}
              {signUpMode && (
                <>Il semblerait que vous n'ayez pas encore de compte. Veuillez en créer un pour continuer.</>
              )}
              {premiereCo && (
                <>
                  Votre compte a été trouvé. Comme il s'agit de votre première connexion, un email vient de vous être
                  envoyé pour réinitialiser votre mot de passe tout en garantissant votre identité. Pensez à vérifier
                  également dans les courriers indésirables si vous ne le voyez pas.
                </>
              )}
            </div>
          </div>
        )}

        <Label htmlFor='email'>Email</Label>
        <Input
          id='email'
          className='mt-0.5'
          type='email'
          readOnly={signUpMode || hasAccount}
          required
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
        />
        {(hasAccount || signUpMode || premiereCo) && (
          <div className='mt-3'>
            <Label htmlFor='password'>Mot de passe</Label>
            <Input
              id='password'
              className='mt-0.5'
              type='password'
              required
              color='red'
              value={password}
              minLength={8}
              maxLength={32}
              onChange={(e) => setPassword(e.currentTarget.value)}
            />
          </div>
        )}
        {hasAccount && (
          <div className='text-right'>
            <ResetPasswordDialog initialEmail={email.trim().toLowerCase()} />
          </div>
        )}
        {signUpMode && (
          <div className='mt-3'>
            <Label htmlFor='confirmPassword'>Confirmer le mot de passe</Label>
            <Input
              minLength={8}
              maxLength={32}
              id='confirmPassword'
              className='mt-0.5'
              type='password'
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.currentTarget.value)}
            />
          </div>
        )}

        {!!error && (
          <div className='bg-red-200 text-red-900 mt-4 rounded-md text-sm p-2' key={"erreur"}>
            {error}
          </div>
        )}
        <Button className='w-full mt-7' type='submit' loading={loading}>
          {signUpMode ? "Créer le compte" : hasAccount || premiereCo ? "Se connecter" : "Valider"}
        </Button>
      </form>
    </div>
  );
};

export default AuthPage;
